type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Map a Supabase auth error to a user-facing message, mirroring the web
 * AuthErrorAlert which keys translations off the error message/code
 * (e.g. "Invalid login credentials", "Email not confirmed", "same_password").
 *
 * Native toasts can't render the embedded link components the web alert uses
 * (<ResetPasswordLink/> etc.), so any markup tags in the resolved string are
 * stripped. When the code has no specific translation, the provided fallback
 * is returned.
 *
 * @param error    The thrown error. `useSignInWithEmailPassword` and the other
 *                 supabase hooks re-throw `response.error.message`, which is the
 *                 GoTrue code/message used as the translation key.
 * @param t        i18next translate function.
 * @param fallback Translation key used when no specific mapping exists.
 *                 Defaults to the generic "couldn't authenticate" message.
 */
export function resolveAuthErrorMessage(
  error: unknown,
  t: Translate,
  fallback = 'auth:errors.generic',
): string {
  const fallbackMessage = t(fallback);
  const code = error instanceof Error ? error.message : String(error ?? '');

  if (!code) return fallbackMessage;

  const translated = t(`auth:errors.${code}`, { defaultValue: '' });

  if (!translated) return fallbackMessage;

  // Strip any link/markup tags a toast can't render (e.g. <ResetPasswordLink>).
  const cleaned = translated.replace(/<\/?[^>]+>/g, '').trim();

  return cleaned || fallbackMessage;
}

/**
 * Set of GoTrue error codes that are safe to surface on the password-reset
 * request screen. The reset flow must NOT confirm whether an email exists
 * (enumeration), so only operational errors like rate-limiting are shown;
 * everything else falls through to the generic message.
 */
const RESET_SAFE_CODES = new Set([
  'over_email_send_rate_limit',
  'over_request_rate_limit',
  'email_provider_disabled',
]);

/**
 * Enumeration-safe variant for the password-reset request screen: only reveals
 * operational errors (rate limits, provider disabled). For any other code —
 * including ones that would imply whether the email exists — it returns the
 * generic message.
 */
export function resolveResetRequestErrorMessage(
  error: unknown,
  t: Translate,
): string {
  const code = error instanceof Error ? error.message : String(error ?? '');

  if (RESET_SAFE_CODES.has(code)) {
    return resolveAuthErrorMessage(error, t);
  }

  return t('auth:errors.generic');
}

/**
 * Identify if an error is a standard network blip (offline, DNS failure, etc)
 * typical of fetch() failures in Safari/WebKit on mobile.
 */
export function isNetworkBlip(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  // Only target the exact browser network drop exceptions
  if (error.name !== 'TypeError' && error.name !== 'AuthRetryableFetchError') {
    return false;
  }

  const msg = error.message;
  return (
    msg === 'Load failed' ||
    msg.startsWith('Load failed (') || // Supabase sometimes appends the domain
    msg === 'Failed to fetch' ||
    msg === 'Network request failed' ||
    msg.includes('NetworkError')
  );
}

